/** @jsx jsx */
/** @jsxRuntime classic */

import { Interpolation, jsx } from '@emotion/react';
import React, { CSSProperties } from 'react';
import ButtonGroupContext from '../ButtonGroup/ButtonGroupContext';
import { ButtonEvent, OnClickEvent } from '../events/EventProvider';
import useEvents from '../events/useEvents';
import { ThemeConfig } from '../style/theme';
import { TypographyVariant } from '../style/typography';
import useTheme from '../style/useTheme';
import { hex2rgba } from '../utils';

export type Props = React.PropsWithChildren<{
  variant?: 'solid' | 'outline' | 'ghost';
  typography?: TypographyVariant;
  color?: 'primary' | 'secondary' | 'neutral';
  weight?: 'normal' | 'bold';
  dataTestId?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  style?: CSSProperties;
  ariaLabel?: string;
  eventId?: string;
  sx?: React.CSSProperties;
}>;

const DEFAULT_ID = 'Button';

const createButtonBoxShadow = (background: string, label: string) => ({
  boxShadow: `0 2px 4px rgba(0, 0, 0, 0.1), inset 0 0 0 1px ${hex2rgba(
    background,
    0.5
  )}`,
  '&:hover': {
    background: `linear-gradient(0deg, ${hex2rgba(background, 0.1)}, ${hex2rgba(
      background,
      0.1
    )}), #fff`,
    boxShadow: `0 2px 4px rgba(0, 0, 0, 0.1), inset 0 0 0 1px ${hex2rgba(
      background,
      0.5
    )}`,
  },
  '&:active': {
    background: `linear-gradient(0deg, ${hex2rgba(background, 0.3)}, ${hex2rgba(
      background,
      0.3
    )}), #fff`,
    boxShadow: `inset 0 0 0 1px ${hex2rgba(background, 0.5)}`,
  },
  '&:focus': {
    background: `linear-gradient(0deg, ${hex2rgba(background, 0.1)}, ${hex2rgba(
      background,
      0.1
    )}), #fff`,
    boxShadow: `0 0 0 4px ${hex2rgba(
      background,
      0.25
    )}, inset 0 0 0 1px ${hex2rgba(background, 0.5)};`,
  },
  '&:disabled': {
    background: label,
    boxShadow: `inset 0 0 0 1px ${hex2rgba(background, 0.5)}`,
  },
});

const Button: React.FC<Props> = (componentProps: Props) => {
  const { theme, resolveTypography, resolveColor, resolveSpacing } = useTheme();
  const contextProps = React.useContext(ButtonGroupContext);
  const props = { ...contextProps, ...componentProps };
  const {
    eventId,
    dataTestId,
    variant,
    ariaLabel,
    color = 'primary',
    weight = 'bold',
    typography = 'label',
    children,
    onClick,
    sx,
    ...rest
  } = props;

  const triggerEvent = useEvents<OnClickEvent<ButtonEvent, EventTarget>>(
    eventId,
    'Button'
  );

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onClick) {
      onClick(e);
    }
    triggerEvent({
      target: e.target,
    });
  };

  const background = resolveColor(theme.components.button[color].color);
  const label = resolveColor(theme.components.button[color].onColor);
  const { sx: themeSx } = theme.components.button[color];

  const css: Interpolation<ThemeConfig> = {
    ...resolveTypography(typography),
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    background,
    height: '48px',
    minHeight: '48px',
    width: '100%',
    borderRadius: '8px',
    boxShadow:
      '0 2px 4px rgba(0, 0, 0, 0.1), inset 0 0 0 1.2px rgba(0, 0, 0, 0.15)',
    border: 'none',
    cursor: 'pointer',
    color: label,
    /*
     * Add 16px margin bottoms for all sequential buttons except last in sequence
     * &:not(:only-of-type):not(:last-child) {
     */
    '&:not(:last-child)': {
      marginBottom: resolveSpacing(2),
    },
    '&:hover': {
      background: `linear-gradient(0deg, rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1)),
      ${hex2rgba(background, 1)}`,
    },
    '&:active': {
      background: `linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), ${hex2rgba(
        background,
        1
      )}`,
    },
    '&:focus': {
      boxShadow: `0 0 0 4px ${hex2rgba(background, 0.25)}`,
    },
    '&:disabled': {
      /*
       *  Don't allow cursor pointer when disabled
       */
      cursor: 'default',
      opacity: 0.5,
    },
    ...(variant === 'outline' && {
      color: background,
      background: label,
      ...createButtonBoxShadow(background, label),
    }),
    ...(variant === 'ghost' && {
      color: background,
      background: 'white',
      boxShadow: 'unset',
      '&:hover': {
        background: hex2rgba(background, 0.1),
      },
      '&:active': {
        background: hex2rgba(background, 0.2),
      },
      '&:focus': {
        background: 'white',
        boxShadow: `0 0 0 4px ${hex2rgba(background, 0.25)}`,
      },
      '&:disabled': {
        background: 'unset',
      },
    }),
    ...(weight === 'bold' && {
      fontWeight: 600,
    }),
    ...themeSx,
    ...sx,
  };

  return (
    <button
      className="origami-Button"
      css={css}
      type="submit"
      data-testid={dataTestId || DEFAULT_ID}
      aria-label={ariaLabel}
      {...rest}
      onClick={handleClick}
    >
      {children}
    </button>
  );
};

export default Button;
